/**
 * The governed reporting-window label.
 *
 * A reporting window is named by the GOVERNED SCOPE that produced it — the
 * campaign flight, a preset the user selected, a custom range — and never by
 * the route, service, table or internal program the numbers travelled
 * through. Those two things had been the same field.
 *
 * How they became the same field: the canonical summary's `timeframe` carries
 * a `label`, and every caller stamped its own name into it ("Optimus platform
 * tab window", "Optimus flight window", "Current report window"). The summary
 * cache keys on the window's DATES, not on that label, so whichever caller
 * warmed the cache first named the window for every later reader — which is
 * how a YouTube Delivery chart came to be titled "OPTIMUS PLATFORM TAB WINDOW
 * (2026-08-10-2026-08-23)". The name of a request origin inside the reporting
 * spine had become the name of the client's reporting period, in the chart
 * subtitle, the x-axis, the tooltip and the spoken description at once.
 *
 * So this module holds two things:
 *
 *   resolveGovernedReportingWindowScope — decides WHICH governed window is
 *     being reported (flight, selected preset, custom range, reported range)
 *     from scope metadata alone. It never reads a supplied label. Deriving
 *     rather than string-replacing is the point: a Last 7 Days window that
 *     happens to sit inside a flight must not inherit the flight's name.
 *
 *   containsInternalReportingSourceTerm — the backstop. Internal vocabulary
 *     is rejected structurally (snake_case tokens, `*Service`/`*Resolver`
 *     identifiers) as well as by name, so a label minted by a service that
 *     does not exist yet is caught by the same rule.
 *
 * Provenance is preserved, not deleted. The caller-supplied label stays on
 * internal diagnostic fields where auditing reads it; it is simply never the
 * thing a client sees.
 *
 * Byte-identical across Cortex and Quantum: one implementation, or the two
 * portals start naming the same reporting period differently.
 */

/**
 * Internal vocabulary that must never surface in a reporting-window label.
 *
 * Lowercase; matched as substrings against a lowercased label. Terms are
 * concrete implementation identifiers only. Ordinary English that legitimately
 * appears in governed labels ("fallback", "flight", "window", "pending") is
 * deliberately absent — this list rejects labels, so an over-broad term would
 * suppress an honest one.
 */
export const INTERNAL_REPORTING_SOURCE_TERMS = Object.freeze([
  "optimus",
  "snapshot",
  "canonical performance summary",
  "canonicalperformancesummary",
  "analyticstopline",
  "platformanalyticsviewmodel",
  "deliverychartmodel",
  "platform tab",
  "ai_kpi",
  "aikpisnapshot",
  "stackadapt api",
  "sequelize",
  "/api/",
]);

/*
 * Structural tells of an implementation identifier, for names this list has
 * not learned yet. A governed window label is prose and dates: it has no
 * snake_case tokens and no `somethingService` identifiers.
 */
const SNAKE_CASE_TOKEN = /[a-z0-9]+_[a-z0-9_]+/i;
const IMPLEMENTATION_IDENTIFIER =
  /[a-z0-9]+(service|resolver|controller|router|repository|serializer|projection)\b/i;

/** True when a label carries internal source/provenance vocabulary. */
export function containsInternalReportingSourceTerm(value) {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  if (!normalized) return false;
  if (INTERNAL_REPORTING_SOURCE_TERMS.some((term) => normalized.includes(term))) {
    return true;
  }
  return SNAKE_CASE_TOKEN.test(normalized) || IMPLEMENTATION_IDENTIFIER.test(normalized);
}

/**
 * A window label safe to render, or null.
 *
 * Null rather than a scrubbed string: a label with an internal name in it was
 * never describing the governed scope, so there is nothing in it worth
 * keeping. Callers fall back to deriving the scope, which is what they should
 * have done in the first place.
 */
export function sanitizeGovernedWindowLabel(value) {
  const normalized = String(value ?? "").trim();
  if (!normalized) return null;
  return containsInternalReportingSourceTerm(normalized) ? null : normalized;
}

function dateOnly(value) {
  const normalized = String(value ?? "").slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(normalized) ? normalized : null;
}

function normalizeRange(value) {
  const start = dateOnly(value?.start ?? value?.dateStart ?? value?.startDate);
  const end = dateOnly(value?.end ?? value?.dateEnd ?? value?.endDate);
  return start && end ? { start, end } : null;
}

/*
 * Scope metadata only. `label` and `requestLabel` are dropped here rather than
 * ignored downstream, so a label cannot reach the descriptor by another route.
 */
function scopeMetadata(timeframe) {
  if (!timeframe || typeof timeframe !== "object") return null;
  const { label, requestLabel, windowLabel, ...rest } = timeframe;
  return rest;
}

function isSameRange(a, b) {
  return Boolean(a && b && a.start === b.start && a.end === b.end);
}

/**
 * Resolve the governed scope descriptor for a reporting window.
 *
 * Returns a timeframe-shaped descriptor for `describeAnalysisWindow`, which
 * remains the single authority on WORDING. This function only answers which
 * governed window is being described, in this order:
 *
 *   1. A window the user explicitly chose is named as their choice. A manual
 *      selection outranks every default — including a flight it happens to
 *      match — because the user's filter is the scope they are reading.
 *   2. A window whose dates ARE the flight's dates is the campaign flight.
 *      Equality is required: a window merely contained by the flight is a
 *      shorter period and borrowing the flight's name would overstate it.
 *   3. A window with dates and a preset is that preset's selection.
 *   4. A window with dates and nothing identifying it is named by its dates.
 *
 * `reportedTimeframe` supplies the dates when present — the label must state
 * the period the numbers actually cover, not the period that was asked for,
 * when the reporting spine clamped one to the other.
 */
export function resolveGovernedReportingWindowScope({
  requestedTimeframe = null,
  reportedTimeframe = null,
  flightWindow = null,
} = {}) {
  const requested = scopeMetadata(requestedTimeframe);
  const reported = scopeMetadata(reportedTimeframe);
  const flight = normalizeRange(flightWindow);
  const range = normalizeRange(reported) || normalizeRange(requested) || flight;

  const requestedSource = String(requested?.source || "").trim().toLowerCase();
  if (requested?.isExplicitOverride === true && requestedSource !== "live_window_recovery") {
    return { ...requested, ...(range || {}) };
  }

  const identity = requested || reported || {};
  const datePreset = String(identity.datePreset || "").trim().toLowerCase() || null;
  const source =
    String(identity.source || "").trim().toLowerCase() ||
    String(reported?.source || "").trim().toLowerCase() ||
    "";

  if (flight && isSameRange(range, flight)) {
    return {
      ...identity,
      ...flight,
      datePreset: "flight",
      /*
       * A source that already names how the flight was established (approved
       * plan, advertiser record, imported reporting) is kept, because
       * describeAnalysisWindow distinguishes those. Anything else — a
       * reporting-spine basis such as active_campaign_to_date — is replaced,
       * so the label reads "Campaign flight" rather than inheriting a name
       * from the resolution path.
       */
      source: /^(approved_flight|advertiser_flight|imported_reporting)/.test(source)
        ? source
        : "campaign_flight_canonical",
    };
  }

  /*
   * ALL CAMPAIGNS: the advertiser's canonical reporting window. It is neither a
   * campaign flight nor a user override — a portfolio spanning several flights
   * has no single flight, and treating it as "explicit_user" below would label
   * a governed default as something the viewer chose.
   */
  if (range && datePreset === "reporting_window") {
    return {
      ...identity,
      ...range,
      datePreset: "reporting_window",
      isExplicitOverride: false,
      source: /^advertiser_reporting_window/.test(source)
        ? source
        : "advertiser_reporting_window_default",
    };
  }

  if (range && datePreset && datePreset !== "flight") {
    return { ...identity, ...range, datePreset, isExplicitOverride: true, source: "explicit_user" };
  }

  if (range) {
    return { ...identity, ...range, datePreset: null, source: "reported_window" };
  }

  return { ...identity };
}
